// DDA Line Drawing Algorithm
export const getDDALine = (x0, y0, x1, y1) => {
  const points = [];

  // Compute slope m
  const dx = x1 - x0;
  const dy = y1 - y0;
  const m = dx === 0 ? Infinity : dy / dx;

  let step = 0;

  // Case 1: m < 1 → X is the driving axis
  if (Math.abs(m) < 1) {
    let y = y0; // Initial value

    if (x0 <= x1) {
      for (let x = x0; x <= x1; x++, y += m) {
        points.push({
          x,
          y: Math.round(y),
          step: step++,
          decisionParameter: m.toFixed(4),
          decision: `m < 1 (m = ${m.toFixed(4)}): X is driving, y += m`
        });
      }
    } else {
      for (let x = x0; x >= x1; x--, y -= m) {
        points.push({
          x,
          y: Math.round(y),
          step: step++,
          decisionParameter: m.toFixed(4),
          decision: `m < 1 (m = ${m.toFixed(4)}): X is driving, y -= m`
        });
      }
    }
  }
  // Case 2: m >= 1 → Y is the driving axis
  else {
    let x = x0; // Initial value
    const m_inv = dx === 0 ? 0 : 1 / m;

    if (y0 <= y1) {
      for (let y = y0; y <= y1; y++, x += m_inv) {
        points.push({
          x: Math.round(x),
          y,
          step: step++,
          decisionParameter: m_inv.toFixed(4),
          decision: `m ≥ 1 (m = ${m.toFixed(4)}): Y is driving, x += 1/m`
        });
      }
    } else {
      for (let y = y0; y >= y1; y--, x -= m_inv) {
        points.push({
          x: Math.round(x),
          y,
          step: step++,
          decisionParameter: m_inv.toFixed(4),
          decision: `m ≥ 1 (m = ${m.toFixed(4)}): Y is driving, x -= 1/m`
        });
      }
    }
  }

  return points;
};

// Bresenham Line Drawing Algorithm
export const getBresenhamLine = (x0, y0, x1, y1) => {
  const points = [];

  // 1. Compute Δx and Δy
  const dx = x1 - x0;
  const dy = y1 - y0;

  // 2. Take absolute values for magnitude
  const abs_dx = Math.abs(dx);
  const abs_dy = Math.abs(dy);

  // 3. Determine direction (sign)
  const sx = dx >= 0 ? 1 : -1;
  const sy = dy >= 0 ? 1 : -1;

  // CASE 1: |m| < 1  →  X is the driving axis
  if (abs_dx >= abs_dy) {
    // 3. Initialize decision parameter
    // p0 = 2Δy − Δx     (Book formula)
    let p = 2 * abs_dy - abs_dx;

    let x = x0;
    let y = y0;

    for (let k = 0; k <= abs_dx; k++) {
      points.push({
        x,
        y,
        step: k,
        decisionParameter: p,
        decision: p < 0 ? 'p < 0 → y unchanged, p = p + 2Δy' : 'p ≥ 0 → y increases, p = p + 2Δy − 2Δx'
      });

      // If pk < 0 → yk+1 = yk
      if (p < 0) {
        // p(k+1) = p(k) + 2Δy
        p = p + 2 * abs_dy;
      } else {
        // y increases by 1 step
        y = y + sy;
        // p(k+1) = p(k) + 2Δy − 2Δx
        p = p + 2 * abs_dy - 2 * abs_dx;
      }

      // x always increases by 1 step
      x = x + sx;
    }
  }
  // CASE 2: |m| > 1  →  Y is the driving axis
  else {
    // p0 = 2Δx − Δy
    let p = 2 * abs_dx - abs_dy;

    let x = x0;
    let y = y0;

    for (let k = 0; k <= abs_dy; k++) {
      points.push({
        x,
        y,
        step: k,
        decisionParameter: p,
        decision: p < 0 ? 'p < 0 → x unchanged, p = p + 2Δx' : 'p ≥ 0 → x increases, p = p + 2Δx − 2Δy'
      });

      if (p < 0) {
        p = p + 2 * abs_dx;
      } else {
        x = x + sx;
        p = p + 2 * abs_dx - 2 * abs_dy;
      }

      y = y + sy;
    }
  }

  return points;
};

// Midpoint Circle Drawing Algorithm
export const getMidpointCircle = (centerX, centerY, radius) => {
  const pointsArray = [];

  // Step 1: Initial values (book format)
  let x = 0;
  let y = radius;

  // Step 2: Initial decision parameter (D0 = 1 - r)
  let D = 1 - radius;

  let step = 0;

  // Helper to add 8 symmetric points
  const addPoints = (x, y, decisionParam) => {
    const symmetricPoints = [
      { x: centerX + x, y: centerY + y, step: step++, decisionParameter: decisionParam, decision: decisionParam < 0 ? 'D < 0 → Choose East, D = D + 2x + 1' : 'D ≥ 0 → Choose SE, D = D + 2(x-y) + 1' },
      { x: centerX - x, y: centerY + y, step: step++, decisionParameter: decisionParam, decision: decisionParam < 0 ? 'D < 0 → Choose East, D = D + 2x + 1' : 'D ≥ 0 → Choose SE, D = D + 2(x-y) + 1' },
      { x: centerX + x, y: centerY - y, step: step++, decisionParameter: decisionParam, decision: decisionParam < 0 ? 'D < 0 → Choose East, D = D + 2x + 1' : 'D ≥ 0 → Choose SE, D = D + 2(x-y) + 1' },
      { x: centerX - x, y: centerY - y, step: step++, decisionParameter: decisionParam, decision: decisionParam < 0 ? 'D < 0 → Choose East, D = D + 2x + 1' : 'D ≥ 0 → Choose SE, D = D + 2(x-y) + 1' },
      { x: centerX + y, y: centerY + x, step: step++, decisionParameter: decisionParam, decision: decisionParam < 0 ? 'D < 0 → Choose East, D = D + 2x + 1' : 'D ≥ 0 → Choose SE, D = D + 2(x-y) + 1' },
      { x: centerX - y, y: centerY + x, step: step++, decisionParameter: decisionParam, decision: decisionParam < 0 ? 'D < 0 → Choose East, D = D + 2x + 1' : 'D ≥ 0 → Choose SE, D = D + 2(x-y) + 1' },
      { x: centerX + y, y: centerY - x, step: step++, decisionParameter: decisionParam, decision: decisionParam < 0 ? 'D < 0 → Choose East, D = D + 2x + 1' : 'D ≥ 0 → Choose SE, D = D + 2(x-y) + 1' },
      { x: centerX - y, y: centerY - x, step: step++, decisionParameter: decisionParam, decision: decisionParam < 0 ? 'D < 0 → Choose East, D = D + 2x + 1' : 'D ≥ 0 → Choose SE, D = D + 2(x-y) + 1' }
    ];
    pointsArray.push(...symmetricPoints);
  };

  // Step 3: Loop until x > y
  while (x <= y) {
    // Plot symmetric points
    addPoints(x, y, D);

    // Case 1: Dk < 0 (choose East pixel)
    if (D < 0) {
      // Update rule: D = D + 2x + 1
      D = D + 2 * x + 1;
    }
    // Case 2: Dk >= 0 (choose South-East pixel)
    else {
      // Update rule: D = D + 2(x - y) + 1
      D = D + 2 * (x - y) + 1;
      y = y - 1; // Move downward
    }

    // x always increases by 1
    x = x + 1;
  }

  // Remove duplicates while preserving order
  const seen = new Set();
  const points = [];
  for (const p of pointsArray) {
    const key = `${p.x},${p.y}`;
    if (!seen.has(key)) {
      points.push(p);
      seen.add(key);
    }
  }

  return points;
};
